import {
  AUTH0_CLAIMS,
  Permission,
  ROLE_PERMISSIONS,
  SELECTED_CLIENT_STORAGE_KEY,
  UserRole,
} from '@/config/auth0.config'
import { useAuth0 } from '@auth0/auth0-react'
import { useMemo, useSyncExternalStore } from 'react'

// "View as Expert" override for admin users. Stored in localStorage and
// broadcast via a tiny store so every usePermissions() consumer re-renders.
const VIEW_AS_EXPERT_STORAGE_KEY = 'snh_view_as_expert'
const viewAsExpertListeners = new Set<() => void>()

function subscribeViewAsExpert(callback: () => void) {
  viewAsExpertListeners.add(callback)
  return () => viewAsExpertListeners.delete(callback)
}

function getViewAsExpertSnapshot(): boolean {
  return localStorage.getItem(VIEW_AS_EXPERT_STORAGE_KEY) === 'true'
}

export function setViewAsExpert(value: boolean) {
  if (value) {
    localStorage.setItem(VIEW_AS_EXPERT_STORAGE_KEY, 'true')
  } else {
    localStorage.removeItem(VIEW_AS_EXPERT_STORAGE_KEY)
  }
  viewAsExpertListeners.forEach(listener => listener())
}

function readRoles(user: Record<string, unknown> | undefined): string[] {
  if (!user) return []
  const rolesClaim = user[AUTH0_CLAIMS.ROLE]
  if (Array.isArray(rolesClaim)) return rolesClaim.map(String)
  if (typeof rolesClaim === 'string' && rolesClaim.length > 0) return [rolesClaim]
  return []
}

function readTenantIds(user: Record<string, unknown> | undefined): string[] {
  if (!user) return []
  const tenantListClaim = user[AUTH0_CLAIMS.TENANT_IDS]
  if (Array.isArray(tenantListClaim)) return tenantListClaim.map(String).filter(Boolean)
  const tenantSingle = user[AUTH0_CLAIMS.TENANT_ID]
  if (typeof tenantSingle === 'string' && tenantSingle.trim()) return [tenantSingle.trim()]
  return []
}

function readLegacyClientIds(user: Record<string, unknown> | undefined): string[] {
  if (!user) return []
  const listClaim = user[AUTH0_CLAIMS.CLIENT_IDS]
  if (Array.isArray(listClaim)) return listClaim.map(String).filter(Boolean)
  const single = user[AUTH0_CLAIMS.CLIENT_ID]
  if (typeof single === 'string' && single.trim()) return [single.trim()]
  return []
}

export function usePermissions() {
  const { user, isAuthenticated } = useAuth0()

  const actualRole = useMemo(() => {
    if (!isAuthenticated || !user) return null
    const roles = readRoles(user as Record<string, unknown>)

    if (roles.includes('Public-Records-Admin')) return 'Public-Records-Admin' as UserRole
    if (roles.includes('Public-Records-Expert')) return 'Public-Records-Expert' as UserRole
    if (roles.includes('Public-Records-Executive')) return 'Public-Records-Executive' as UserRole
    if (roles.includes('Client-Admin')) return 'Client-Admin' as UserRole
    if (roles.includes('Client-User')) return 'Client-User' as UserRole

    return (roles[0] as UserRole) ?? null
  }, [user, isAuthenticated])

  const viewAsExpert = useSyncExternalStore(subscribeViewAsExpert, getViewAsExpertSnapshot)
  const isActualAdmin = actualRole === 'Public-Records-Admin'

  // Admins can preview the dashboard as an expert; everyone else keeps their real role.
  const userRole: UserRole | null =
    isActualAdmin && viewAsExpert ? 'Public-Records-Expert' : actualRole

  const assignedTenantIds = useMemo(
    () => readTenantIds(user as Record<string, unknown>),
    [user],
  )

  const assignedClientIds = useMemo(
    () => readLegacyClientIds(user as Record<string, unknown>),
    [user],
  )

  const clientId = assignedClientIds[0] ?? null
  const tenantId = assignedTenantIds[0] ?? null
  const clientName = useMemo(() => {
    const name = user?.[AUTH0_CLAIMS.CLIENT_NAME]
    return typeof name === 'string' ? name : null
  }, [user])

  const permissions = useMemo(() => {
    if (!userRole) return []
    return ROLE_PERMISSIONS[userRole] || []
  }, [userRole])

  const hasPermission = (permission: Permission): boolean => permissions.includes(permission)

  const hasAnyPermission = (permissionsToCheck: Permission[]): boolean =>
    permissionsToCheck.some(p => permissions.includes(p))

  const hasAllPermissions = (permissionsToCheck: Permission[]): boolean =>
    permissionsToCheck.every(p => permissions.includes(p))

  const isAdmin = userRole === 'Public-Records-Admin'
  const isExpert = userRole === 'Public-Records-Expert'
  const isExecutive = userRole === 'Public-Records-Executive'
  const isClientAdmin = userRole === 'Client-Admin'
  const isClientUser = userRole === 'Client-User'
  const isExternalUser = isClientAdmin || isClientUser
  const isInternalUser = isAdmin || isExpert || isExecutive

  const hasLegacyClientAssignmentsOnly =
    assignedTenantIds.length === 0 && assignedClientIds.length > 0

  const canAccessAllClients =
    isAdmin &&
    (
      assignedTenantIds.length === 0
      || assignedTenantIds.includes('*')
      || assignedClientIds.includes('*')
    )
  const canSwitchClient = canAccessAllClients || assignedTenantIds.length > 1

  const selectedClientId =
    typeof window !== 'undefined'
      ? localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY)
      : null

  const effectiveClientId = canSwitchClient
    ? (selectedClientId || (canAccessAllClients ? null : tenantId))
    : tenantId

  const effectiveTenantId = effectiveClientId
  const selectedTenantId = selectedClientId

  const effectiveClientLabel = canSwitchClient
    ? (
      canAccessAllClients
        ? (selectedClientId || 'Portfolio View')
        : (clientName || effectiveClientId || 'Your Organization')
    )
    : clientName || tenantId || 'Your Organization'

  return {
    userRole,
    actualRole,
    isActualAdmin,
    viewAsExpert,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isExpert,
    isExecutive,
    isClientAdmin,
    isClientUser,
    isExternalUser,
    isInternalUser,
    tenantId,
    assignedTenantIds,
    hasLegacyClientAssignmentsOnly,
    selectedTenantId,
    effectiveTenantId,
    clientId,
    clientName,
    assignedClientIds,
    canAccessAllClients,
    canSwitchClient,
    selectedClientId,
    effectiveClientId,
    effectiveClientLabel,
    canViewPII: hasPermission('view:pii'),
    canExportFullData: hasPermission('export:full-data'),
    canExportFilteredData: hasPermission('export:filtered-data'),
    canManageSettings: hasPermission('manage:settings'),
    canExport: hasPermission('export:full-data') || hasPermission('export:filtered-data'),
  }
}
